import { QuestionRegulatoryData, Questionnaire, QuestionnaireQuestion } from "../../Models/Surveys/SurveyDataModel";
import { BaseRegulationLogicManager } from "../BaseRegulationLogicManager";
import ResourcesConstants from "../ResourceConstants";
import { ComplianceScoreResult, InventoryDetailsResponse } from "../../StateControllers/Survey/SurveyResponse/SurveyResponseModels";

export class CaProp65CustomLogicManager extends BaseRegulationLogicManager {
    public static CaProp65RegulationId = "e1d5cb06-e7ce-4b11-a741-e72f96e3b739";

    private readonly ContainsChemicalKey: string = "cal_65_contains_chemical";
    private readonly RequiresDisclosureKey: string = "cal_65_requires_disclosure";
    private readonly WarningTextKey: string = "cal_65_warning_text";
    private readonly NoWarningReasonKey: string = "cal_65_no_warning_reason";
    private readonly SupplierComments: string = "cal_65_supplier_comments";
    private readonly IntendedToBeSoldInUsQuestion = "Is the product intended to be sold into the United States?";
    private readonly IntendedToBeSoldInCalifornia = "Is the product intended to be sold into the State of California?";

    constructor(questions: QuestionnaireQuestion[], overallQuestionnaire: Questionnaire, questionRegulatoryData: QuestionRegulatoryData[], selectedInventory: InventoryDetailsResponse | null, complianceScoreResult: ComplianceScoreResult | null) {
        super(questions, overallQuestionnaire, questionRegulatoryData, selectedInventory, complianceScoreResult);
    }

    protected getAdditionalQuestionIndicesToHide = (question: QuestionnaireQuestion, questionIndex: number, indicesToHideOrShow: boolean[]) => {
        if (!this.shouldShowCaProp65()) {
            return this._questions.map(_ => true);
        } else if (this._questions.every(q => q.hidden)) {
            indicesToHideOrShow = this._questions.map(_ => false);
        }

        let childQuestion1;
        let childQuestion2;

        switch (question.summaryFieldKey) {
            case this.ContainsChemicalKey: {
                childQuestion2 = this._questions.findIndex(q => q.summaryFieldKey === this.NoWarningReasonKey);
                let firstQuestionValue = this._questions.find(q => q.summaryFieldKey == this.RequiresDisclosureKey)?.value ?? "";
                indicesToHideOrShow[childQuestion2] = question.value !== ResourcesConstants.NativeAnswerTexts.YesAnswer || firstQuestionValue !== ResourcesConstants.NativeAnswerTexts.NoAnswer;

                break;
            }
            case this.RequiresDisclosureKey: {
                childQuestion1 = this._questions.findIndex(q => q.summaryFieldKey === this.WarningTextKey);
                childQuestion2 = this._questions.findIndex(q => q.summaryFieldKey === this.NoWarningReasonKey);
                let firstQuestionValue = this._questions.find(q => q.summaryFieldKey == this.ContainsChemicalKey)?.value ?? "";

                indicesToHideOrShow[childQuestion1] = question.value !== ResourcesConstants.NativeAnswerTexts.YesAnswer;
                indicesToHideOrShow[childQuestion2] = question.value !== ResourcesConstants.NativeAnswerTexts.NoAnswer || firstQuestionValue !== ResourcesConstants.NativeAnswerTexts.YesAnswer;

                break;
            }
        }

        return indicesToHideOrShow;
    }

    public customIsQuestionRequired(question: QuestionnaireQuestion) {
        if (question.summaryFieldKey === this.NoWarningReasonKey) {
            let firstQuestionValue = this._questions.find(q => q.summaryFieldKey === this.ContainsChemicalKey)?.value ?? "";
            let secondQuestionValue = this._questions.find(q => q.summaryFieldKey === this.RequiresDisclosureKey)?.value ?? "";

            if (firstQuestionValue?.toLowerCase() == ResourcesConstants.NativeAnswerTexts.YesAnswer.toLowerCase() && secondQuestionValue?.toLowerCase() == ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase()) {
                return true;
            }
        }
        else if (question.summaryFieldKey === this.SupplierComments) {
            const dependentQuestionAnwer = this._questions.find(q => q.summaryFieldKey === this.NoWarningReasonKey)?.value ?? "";

            return dependentQuestionAnwer.toLowerCase() === ResourcesConstants.NativeAnswerTexts.Other.toLowerCase();
        }

        return false;
    }

    private shouldShowCaProp65(): boolean {
        let result = true;
        const intendedMarketsPage = this._selectedInventory?.questionnaireList.selectMany(q => q.pages).find(p => p.title == "Intended Markets");
        const intendedToBeSoldInUsQuestion = intendedMarketsPage?.questions.find(q => q.text === this.IntendedToBeSoldInUsQuestion)?.value.toLowerCase();
        const intendedToBeSoldInCalifornia = intendedMarketsPage?.questions.find(q => q.text === this.IntendedToBeSoldInCalifornia)?.value.toLowerCase();

        if (intendedToBeSoldInCalifornia === ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase() || intendedToBeSoldInUsQuestion === ResourcesConstants.NativeAnswerTexts.NoAnswer.toLowerCase()) {
            result = false;
        }

        return result;
    }

    public getPageRequiredInfoLocalized() {
        if (this.shouldShowCaProp65()) {
            return null;
        }

        return "California Proposition 65 data is not required because product was declared as not intended to be sold in the United States or California. If this is not correct, update Intended Markets."
    }
}
